import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LoggerService } from '@core/services/logger.service';
import { TranslateLoader, type TranslationObject } from '@ngx-translate/core';
import { catchError, forkJoin, map, of, type Observable } from 'rxjs';
import { NAMESPACES } from './translate-namespaces.generated';

function toNamespaceKey(namespace: string): string {
  return namespace.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

@Injectable()
export class NamespaceTranslateLoader extends TranslateLoader {
  // Translation files are static assets: HttpBackend skips the interceptor
  // chain so requests keep their /i18n/ URL and carry no auth headers.
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly logger = inject(LoggerService);

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin(
      NAMESPACES.map((namespace) => this.loadNamespace(lang, namespace))
    ).pipe(map((parts) => Object.assign({}, ...parts) as TranslationObject));
  }

  /**
   * Loads one namespace file, degrading to an empty namespace on failure.
   *
   * A namespace is isolated on purpose: `forkJoin` errors as soon as any source
   * errors, so letting a single missing or malformed file through would discard
   * every other namespace and leave the whole app rendering raw keys. Failing
   * loudly here but continuing keeps the blast radius to the namespace at fault.
   */
  private loadNamespace(lang: string, namespace: string): Observable<TranslationObject> {
    const url = `/i18n/${lang}/${namespace}.json`;

    return this.http.get<TranslationObject>(url).pipe(
      map((translations) => {
        // A missing file is a 404 on the dev server, but a bucket configured with
        // a SPA fallback answers 200 with index.html instead — that body reaches
        // here as a string rather than an HttpErrorResponse, so guard the shape
        // as well as the status.
        if (translations === null || typeof translations !== 'object' || Array.isArray(translations)) {
          throw new Error(`expected a JSON object, received ${Array.isArray(translations) ? 'an array' : typeof translations}`);
        }

        return { [toNamespaceKey(namespace)]: translations };
      }),
      catchError((error: unknown) => {
        const reason = error instanceof HttpErrorResponse ? `HTTP ${error.status}` : (error as Error).message;

        this.logger.error(
          `[i18n] Namespace "${namespace}" failed to load for "${lang}" (${url}): ${reason}. ` +
            `Every key under "${toNamespaceKey(namespace)}.*" will render as its raw key. ` +
            `Check that ${url} exists and is valid JSON for every supported language. ` +
            `The namespace list is generated from the fallback locale by ` +
            `_scripts/i18n/generate-namespaces.js — run \`pnpm i18n:namespaces\` if it is stale.`,
          error
        );

        return of({ [toNamespaceKey(namespace)]: {} } as TranslationObject);
      })
    );
  }
}
